import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Check, X as XIcon, Archive, Trash2, MapPin } from "lucide-react";

const TYPE_TABS = [
  { v: "all", label: "All types" },
  { v: "volunteer_opportunity", label: "Volunteer" },
  { v: "organization", label: "Organizations" },
  { v: "housing_project", label: "Housing" },
  { v: "help_offer", label: "Help offers" },
];

const STATUS_TABS = [
  { v: "all", label: "All statuses" },
  { v: "pending_review", label: "Pending" },
  { v: "approved", label: "Approved" },
  { v: "rejected", label: "Rejected" },
  { v: "archived", label: "Archived" },
];

const STATUS_STYLES = {
  pending_review: { bg: "#F5C518", fg: "#1F1B16", label: "PENDING" },
  approved: { bg: "#3C9A5F", fg: "#FDFBF7", label: "APPROVED" },
  rejected: { bg: "#B23A3A", fg: "#FDFBF7", label: "REJECTED" },
  archived: { bg: "#7A7A7A", fg: "#FDFBF7", label: "ARCHIVED" },
};

const TYPE_LABELS = {
  volunteer_opportunity: "Volunteer Opportunity",
  organization: "Organization",
  housing_project: "Housing Project",
  help_offer: "Help Offer",
};

const ACTION_LABELS = {
  submitted: "Submitted",
  moderate_approved: "Approved",
  moderate_rejected: "Rejected",
  moderate_archived: "Archived",
};

export default function AdminPublicSubmissions() {
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");
  const [items, setItems] = useState([]);
  const [countsByType, setCountsByType] = useState({});
  const [counts, setCounts] = useState({});
  const [selected, setSelected] = useState(null);
  const [comment, setComment] = useState("");

  const load = async () => {
    try {
      const { data } = await http.get("/admin/public-submissions", { params: { type, status } });
      setItems(data.items);
      setCounts(data.counts);
      setCountsByType(data.counts_by_type);
    } catch { toast.error("Failed to load public submissions"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [type, status]);

  const act = async (newStatus) => {
    try {
      await http.patch(`/admin/public-submissions/${selected.id}`, { status: newStatus, comment });
      toast.success(`Marked ${newStatus}`);
      setSelected(null); setComment("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async () => {
    if (!window.confirm(`Delete this submission permanently?`)) return;
    try {
      await http.delete(`/admin/public-submissions/${selected.id}`, { params: { comment } });
      toast.success("Deleted");
      setSelected(null); setComment("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const bulkDeleteTest = async () => {
    const match = window.prompt("Delete ALL public submissions whose title/name/description contains this phrase (case-insensitive). Type the phrase (e.g. 'test'):", "test");
    if (!match) return;
    try {
      const { data } = await http.post(`/admin/public-submissions/bulk-delete`, null, { params: { match } });
      toast.success(`Deleted ${data.deleted} submission(s)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const title = (it) => (it.data || {}).title || (it.data || {}).name || "(no title)";
  const desc = (it) => (it.data || {}).description || "";
  const city = (it) => (it.data || {}).city || "";
  const country = (it) => (it.data || {}).country || "";

  return (
    <div data-testid="admin-public-submissions-panel">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="overline mb-2">PUBLIC SUBMISSIONS</div>
          <h2 className="font-display text-3xl">Volunteer / Org / Housing / Help offers</h2>
        </div>
        <button onClick={bulkDeleteTest} data-testid="admin-pubsub-bulk-delete" className="wofy-btn-ghost text-xs" style={{ color: "#B23A3A" }}>
          <Trash2 className="w-3 h-3" /> Bulk delete test posts
        </button>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {TYPE_TABS.map((t) => (
          <button key={t.v} onClick={() => setType(t.v)} data-testid={`admin-pubsub-type-${t.v}`}
                  className={`px-3 py-1.5 text-sm rounded-full ${type === t.v ? "" : "hover:bg-[color:var(--wofy-surface)]"}`}
                  style={type === t.v ? { background: "var(--wofy-primary)", color: "#FDFBF7" } : {}}>
            {t.label} <span className="ml-2 text-xs font-mono opacity-60">{t.v === "all" ? (counts.all || 0) : (countsByType[t.v] || 0)}</span>
          </button>
        ))}
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {STATUS_TABS.map((s) => (
          <button key={s.v} onClick={() => setStatus(s.v)} data-testid={`admin-pubsub-status-${s.v}`}
                  className={`px-3 py-1 text-xs rounded-full border ${status === s.v ? "" : "hover:bg-[color:var(--wofy-surface)]"}`}
                  style={status === s.v ? { background: "var(--wofy-text)", color: "#FDFBF7", borderColor: "transparent" } : { borderColor: "var(--wofy-border)" }}>
            {s.label} <span className="ml-1 opacity-60 font-mono">{s.v === "all" ? (counts.all || 0) : (counts[s.v] || 0)}</span>
          </button>
        ))}
      </div>

      <div className="mt-6 grid gap-3">
        {items.length === 0 ? (
          <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>Nothing here yet.</p>
        ) : items.map((it) => {
          const st = STATUS_STYLES[it.status] || { bg: "#666", fg: "#fff", label: (it.status || "").toUpperCase() };
          const isPending = it.status === "pending_review";
          return (
            <div key={it.id} data-testid={`admin-pubsub-${it.id}`} className="wofy-card cursor-pointer hover:shadow-md" onClick={() => { setSelected(it); setComment(""); }} style={isPending ? { borderLeft: "4px solid #F5C518" } : {}}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-block px-2 py-0.5 rounded text-[10px] font-mono uppercase font-semibold" style={{ background: st.bg, color: st.fg }}>{st.label}</span>
                <span className="wofy-tag text-[10px]">{TYPE_LABELS[it.type] || it.type}</span>
                {(it.data?.category || it.data?.housing_type || it.data?.offer_type) && (
                  <span className="text-[10px] font-mono uppercase" style={{ color: "var(--wofy-text-soft)" }}>
                    {it.data?.category || it.data?.housing_type || it.data?.offer_type}
                  </span>
                )}
                {it.captcha_verified === false && (
                  <span title={`Captcha: ${it.captcha_detail || 'not verified'}`} className="inline-block px-2 py-0.5 rounded text-[10px] font-mono uppercase" style={{ background: "#B23A3A", color: "#FDFBF7" }}>
                    ⚠ NO CAPTCHA
                  </span>
                )}
              </div>
              <div className="font-display text-lg mt-1">{title(it)}</div>
              <p className="text-sm mt-1 line-clamp-2" style={{ color: "var(--wofy-text-soft)" }}>{desc(it)}</p>
              <div className="mt-2 text-[11px] font-mono flex flex-wrap items-center gap-x-3 gap-y-1" style={{ color: "var(--wofy-text-soft)" }}>
                <span>SUBMITTED: {new Date(it.created_at).toLocaleString()}</span>
                <span>· by {it.submitter_name} &lt;{it.submitter_email}&gt;</span>
                {city(it) && <span className="inline-flex items-center gap-1">· <MapPin className="w-3 h-3" /> {city(it)}, {country(it)}</span>}
              </div>

              {/* Action-taken box */}
              <div className="mt-3 rounded-md border px-3 py-2 text-xs" style={{ borderColor: isPending ? "#F5C518" : "var(--wofy-border)", background: isPending ? "rgba(245,197,24,0.08)" : "var(--wofy-surface)" }}>
                {isPending ? (
                  <>
                    <span className="font-mono uppercase tracking-wider font-semibold" style={{ color: "#8A6D00" }}>Pending review</span>
                    <span className="ml-2" style={{ color: "var(--wofy-text-soft)" }}>— awaiting moderator action</span>
                  </>
                ) : (
                  <div>
                    <div className="font-mono uppercase tracking-wider">
                      {ACTION_LABELS[it.last_action] || it.last_action}
                      {it.last_action_at && <span className="ml-2" style={{ color: "var(--wofy-text-soft)" }}>· {new Date(it.last_action_at).toLocaleString()}</span>}
                      {it.last_action_admin && <span className="ml-2" style={{ color: "var(--wofy-text-soft)" }}>· by {it.last_action_admin}</span>}
                    </div>
                    {it.last_action_comment ? (
                      <div className="mt-1 italic" style={{ color: "var(--wofy-text)" }}>"{it.last_action_comment}"</div>
                    ) : (
                      <div className="mt-1" style={{ color: "var(--wofy-text-soft)" }}>No comment provided.</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {selected && (
        <div className="fixed inset-0 z-40 bg-[#14213D]/25 backdrop-blur-sm" onClick={() => setSelected(null)}>
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-[720px] overflow-y-auto p-8" style={{ background: "var(--wofy-paper)" }} onClick={(e) => e.stopPropagation()} data-testid="admin-pubsub-drawer">
            <div className="flex items-start justify-between">
              <div>
                <div className="overline">{TYPE_LABELS[selected.type] || selected.type}</div>
                <h3 className="font-display text-2xl mt-1">{title(selected)}</h3>
                <div className="text-sm mt-1" style={{ color: "var(--wofy-text-soft)" }}>
                  Submitted by {selected.submitter_name} · {selected.submitter_email} {selected.submitter_phone ? `· ${selected.submitter_phone}` : ""}
                </div>
              </div>
              <button onClick={() => setSelected(null)} className="wofy-btn-ghost text-xs">Close</button>
            </div>

            {/* Full data dump */}
            <div className="mt-6 wofy-card">
              <div className="text-xs font-mono uppercase tracking-wider mb-2" style={{ color: "var(--wofy-text-soft)" }}>Details</div>
              <div className="grid gap-2 text-sm">
                {Object.entries(selected.data || {}).map(([k, v]) => (
                  <div key={k} className="grid grid-cols-4 gap-2">
                    <div className="col-span-1 font-mono uppercase text-[11px]" style={{ color: "var(--wofy-text-soft)" }}>{k.replace(/_/g, " ")}</div>
                    <div className="col-span-3 whitespace-pre-wrap break-words">
                      {typeof v === "boolean" ? (v ? "Yes" : "No")
                        : Array.isArray(v) ? v.join(", ")
                        : typeof v === "object" && v !== null ? <pre className="text-xs">{JSON.stringify(v, null, 2)}</pre>
                        : String(v ?? "")}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 wofy-card">
              <div className="text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>Moderation note (recorded & emailed to submitter)</div>
              <textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} data-testid="admin-pubsub-comment" className="w-full mt-2 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} placeholder="e.g. Verified with local partner. Publishing." />
              <div className="mt-3 flex flex-wrap gap-2">
                <button onClick={() => act("approved")} data-testid="admin-pubsub-approve" className="wofy-btn-primary text-sm"><Check className="w-4 h-4" /> Approve</button>
                <button onClick={() => act("rejected")} data-testid="admin-pubsub-reject" className="wofy-btn-ghost text-sm" style={{ color: "#B23A3A" }}><XIcon className="w-4 h-4" /> Reject</button>
                <button onClick={() => act("archived")} className="wofy-btn-ghost text-sm"><Archive className="w-4 h-4" /> Archive</button>
                <button onClick={del} className="wofy-btn-ghost text-sm" style={{ color: "#B23A3A" }}><Trash2 className="w-4 h-4" /> Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
